import React, { FC, useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { Grid } from '@mui/material'
import { CompanyBankAccountsDto, PortfolioCurrenciesEnum } from '@sparling/financial-common'

import { Modal } from '../../../../components/Modal'
import { Loader } from '../../../../components/Loader'
import { createEnum } from '../../../../utils/createEnum'
import { InputField } from '../../../../components/InputField'
import { InputSelect } from '../../../../components/InputSelect'
import { GreenCheckBox } from '../../../../components/CheckBox'

interface Props {
  isOpen: boolean
  modalClose: () => void
  companyId: number
  companyBankAccountData?: CompanyBankAccountsDto
}

type FormInputs = {
  id?: number
  companyId: number
  currency: PortfolioCurrenciesEnum
  bankName: string
  bankSwift: string
  bankAccountNumber: number
  bankAddress: string
  description: string
  beneficiaryName: string
  intermediaryBankName: string
  intermediaryBankSwift: string
  intermediaryBankAddress: string
  referenceToInclude: string
  isPrimary: boolean
}

const ShowCompanyAccountModal: FC<Props> = ({ isOpen, modalClose, companyId, companyBankAccountData }) => {
  const formMethods = useForm<FormInputs>({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    shouldFocusError: true,
  })
  const [loading, setLoading] = useState<boolean>(false)

  const { setValue, reset } = formMethods

  useEffect(() => {
    if (isOpen) {
      reset()

      if (companyBankAccountData) {
        setValue('id', companyBankAccountData.id)
      }

      setLoading(false)
    }
  }, [isOpen, companyId, reset, setValue, companyBankAccountData])

  return (
    <Modal
      title={'Account Details'}
      isOpen={isOpen}
      modalClose={modalClose}
      cancelTitle={'Close'}
      width={680}
      submitButtonProps={{ visible: false }}
    >
      {loading && <Loader />}
      <FormProvider {...formMethods}>
        <Grid container spacing={2.5}>
          <Grid item xs={6} container direction="column">
            <InputSelect
              labelName={'Currency'}
              inputName="currency"
              placeholder="Please Select"
              enums={createEnum(PortfolioCurrenciesEnum)}
              width={300}
              mb={20}
              defaultValue={companyBankAccountData?.currency}
            />
            <InputField
              labelName={'Bank Name'}
              inputName="bankName"
              inputType="text"
              defaultValue={companyBankAccountData?.bankName}
            />
            <InputField
              labelName={'Bank SWIFT/BIC'}
              inputName="bankSwift"
              inputType="text"
              defaultValue={companyBankAccountData?.bankSwift}
            />
            <InputField
              labelName={'Bank Account Number'}
              inputName="bankAccountNumber"
              inputType="text"
              defaultValue={companyBankAccountData?.bankAccountNumber}
            />
            <InputField
              labelName={'Bank Address'}
              inputName="bankAddress"
              inputType="text"
              defaultValue={companyBankAccountData?.bankAddress}
            />
            <InputField
              labelName={'Description'}
              inputName="description"
              inputType="text"
              defaultValue={companyBankAccountData?.description}
            />
          </Grid>

          <Grid item xs={6} container direction="column">
            <InputField
              labelName={'Beneficiary Name'}
              inputName="beneficiaryName"
              inputType="text"
              defaultValue={companyBankAccountData?.beneficiaryName}
            />
            <InputField
              labelName={'Intermediary Bank Name'}
              inputName="intermediaryBankName"
              inputType="text"
              defaultValue={companyBankAccountData?.intermediaryBankName}
            />
            <InputField
              labelName={'Intermediary Bank SWIFT/BIC'}
              inputName="intermediaryBankSwift"
              inputType="text"
              defaultValue={companyBankAccountData?.intermediaryBankSwift}
            />
            <InputField
              labelName={'Intermediary Bank Address'}
              inputName="intermediaryBankAddress"
              inputType="text"
              defaultValue={companyBankAccountData?.intermediaryBankAddress}
            />
            <InputField
              labelName={'Reference to Include'}
              inputName="referenceToInclude"
              inputType="text"
              defaultValue={companyBankAccountData?.referenceToInclude}
            />
            <GreenCheckBox
              checkBoxName={'isPrimary'}
              checkBoxLabel={'Is Primary'}
              styles={{ paddingTop: 20 }}
              status={companyBankAccountData?.isPrimary}
            />
          </Grid>
        </Grid>
      </FormProvider>
    </Modal>
  )
}

export const MemoShowCompanyAccountModal = React.memo(ShowCompanyAccountModal)
